import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@nxsys/store';
import { ME_PERMISSION, MePermissionType } from '@nxsys/constants/mePermission';
import {
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
} from '@nxsys/helpers/permissionHelper';

const usePermissions = () =>
  useSelector((state: RootState) => state.appStore.permissions);

// Personal profile permissions
export const useIsAllowedPersonalDetailsUpdate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.PERSONAL_DETAILS_UPDATE);
};

export const useIsAllowedContactUpdate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.CONTACT_DETAILS_UPDATE);
};

export const useIsAllowedAddressUpdate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.ADDRESS_UPDATE);
};

export const useIsAllowedBankDetailsUpdate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.BANK_DETAILS_UPDATE);
};

export const useIsAllowedHolidayPayShow = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.HOLIDAY_PAY_DETAILS_SHOW);
};

// Work-related permissions
export const useIsAllowedTimesheetCreate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.TIMESHEETS_CREATE);
};

export const useIsAllowedDocumentCreate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.DOCUMENTS_CREATE);
};

export const useIsAllowedExpenseCreate = () => {
  const permissions = usePermissions();
  return hasPermission(permissions, ME_PERMISSION.EXPENSE_CREATE);
};

// Generic hook for checking custom permissions
export const useHasPermission = (permission: MePermissionType) => {
  const permissions = usePermissions();
  return hasPermission(permissions, permission);
};

export const useHasAllPermissions = (
  requiredPermissions: MePermissionType[],
) => {
  const permissions = usePermissions();
  return hasAllPermissions(permissions, requiredPermissions);
};

export const useHasAnyPermission = (
  requiredPermissions: MePermissionType[],
) => {
  const permissions = usePermissions();
  return hasAnyPermission(permissions, requiredPermissions);
};

// Combined hook returning all employee permission flags
export const useEmployeePermissions = () => {
  const permissions = usePermissions();

  return useMemo(
    () => ({
      canUpdatePersonalDetails: hasPermission(
        permissions,
        ME_PERMISSION.PERSONAL_DETAILS_UPDATE,
      ),
      canUpdateContact: hasPermission(
        permissions,
        ME_PERMISSION.CONTACT_DETAILS_UPDATE,
      ),
      canUpdateAddress: hasPermission(
        permissions,
        ME_PERMISSION.ADDRESS_UPDATE,
      ),
      canUpdateBankDetails: hasPermission(
        permissions,
        ME_PERMISSION.BANK_DETAILS_UPDATE,
      ),
      canShowHolidayPay: hasPermission(
        permissions,
        ME_PERMISSION.HOLIDAY_PAY_DETAILS_SHOW,
      ),
      canCreateTimesheet: hasPermission(
        permissions,
        ME_PERMISSION.TIMESHEETS_CREATE,
      ),
      canCreateDocument: hasPermission(
        permissions,
        ME_PERMISSION.DOCUMENTS_CREATE,
      ),
      canCreateExpense: hasPermission(
        permissions,
        ME_PERMISSION.EXPENSE_CREATE,
      ),
    }),
    [permissions],
  );
};
